import { BgEffectLayer } from "@wa/widgets/bg-effect";
import type { AuthLayoutSpec } from "@wa/core/theme";

/**
 * Animated background OR a photo from the media library for the login/signup page.
 * By default it fills its parent absolutely (`absolute inset-0`) - the layout
 * (full screen vs 50/50 panel) is decided by `AuthScene`. In `fixed` mode the
 * background is pinned to the VIEWPORT (`fixed inset-0`), so while scrolling the long account hub
 * the background neither moves nor stretches to the content height. Brand colors from CSS variables → follows
 * the host theme (light/dark). The same mesh effect the page builder offers.
 */
export function AppBackground({ spec, fixed = false }: { spec?: AuthLayoutSpec; fixed?: boolean }) {
  const effect = spec?.bgEffect ?? "mesh";
  const intensity = spec?.intensity ?? 55;
  const img = spec?.bgImage?.trim();
  const imgDark = spec?.bgImageDark?.trim();

  return (
    <div aria-hidden className={`${fixed ? "fixed" : "absolute"} inset-0 overflow-hidden bg-background`}>
      {img ? (
        <>
          <img
            src={img}
            alt=""
            className={`h-full w-full object-cover ${imgDark ? "dark:hidden" : ""}`}
            draggable={false}
          />
          {imgDark && (
            <img src={imgDark} alt="" className="hidden h-full w-full object-cover dark:block" draggable={false} />
          )}
          {/* Readability scrim so the card/text stays high-contrast over the photo. */}
          <div className="absolute inset-0 bg-background/30" />
        </>
      ) : effect === "none" ? null : (
        <BgEffectLayer
          props={{
            bgType: "solid",
            bgEffect: effect,
            bgEffectColor1: "var(--color-primary)",
            bgEffectColor2: "var(--color-primary-2)",
            bgEffectColor3: "var(--color-accent-brand)",
            bgEffectSpeed: 24,
            bgEffectBlur: 90,
            bgEffectIntensity: intensity,
            bgEffectAnimate: true,
          }}
        />
      )}
    </div>
  );
}
